import { ChatMap, MessageContextMap, bigIntMax, chatIdentifierToString, emptyUnreadCounts, latestMessageExpired, mergeUnreadCounts, } from "@shared";
import {} from "svelte/store";
import { offlineStore } from "../../stores";
import { withPausedStores, writable } from "../../utils/stores";
import { localUpdates } from "../localUpdates";
import { notEq } from "../utils";
const MARK_READ_INTERVAL = 10 * 1000;
export class MessagesRead {
    readUpTo;
    threads = new Map();
    dateReadPinned;
    get threadsList() {
        return [...this.threads.entries()].map(([threadRootMessageIndex, readUpTo]) => ({
            threadRootMessageIndex: Number(threadRootMessageIndex),
            readUpTo,
        }));
    }
    get empty() {
        return (this.readUpTo === undefined &&
            this.threads.size === 0 &&
            this.dateReadPinned === undefined);
    }
    markReadUpTo(index) {
        this.readUpTo = Math.max(this.readUpTo ?? 0, index);
    }
    updateThread(rootIndex, readUpTo) {
        const current = this.threads.get(rootIndex);
        if (current === undefined || current < readUpTo) {
            this.threads.set(rootIndex, readUpTo);
        }
    }
    setThreads(threads) {
        this.threads = threads.reduce((rec, t) => {
            rec.set(t.threadRootMessageIndex, t.readUpTo);
            return rec;
        }, new Map());
    }
    markReadPinned(dateReadPinned) {
        this.dateReadPinned = dateReadPinned;
    }
}
/**
 * Let's try to leave this logic alone as much as we can
 * and just focus on converting the actual underlying state to svelte 5 runes
 */
export class MessageReadTracker {
    #timeout;
    #stopped = false;
    // Bumped whenever the read state of a chat changes, keyed by chat id string
    #versions = new Map();
    #contributions = new Map();
    #store = writable({
        serverState: new ChatMap(),
        waiting: new MessageContextMap(),
        state: new ChatMap(),
    }, undefined, notEq);
    get dirty() {
        return this.#store.dirty;
    }
    get value() {
        return this.#store.value;
    }
    subscribe(sub, invalidate) {
        return this.#store.subscribe(sub, invalidate);
    }
    #triggerLoop(api) {
        if (!this.#stopped) {
            this.#timeout = window.setTimeout(() => this.#sendToServer(api), MARK_READ_INTERVAL);
        }
    }
    start(api) {
        console.log("starting the mark read poller");
        this.#stopped = false;
        if (import.meta.env.OC_NODE_ENV !== "test") {
            this.#triggerLoop(api);
        }
        if (import.meta.env.OC_NODE_ENV !== "test") {
            window.onbeforeunload = () => this.#sendToServer(api);
        }
    }
    stop() {
        console.log("stopping the mark read poller");
        this.#stopped = true;
        if (this.#timeout !== undefined) {
            window.clearTimeout(this.#timeout);
        }
    }
    #sendToServer(api) {
        const req = this.value.state.reduce((req, [chatId, data]) => {
            if (!data.empty) {
                req.push({
                    chatId,
                    readUpTo: data.readUpTo,
                    threads: data.threadsList,
                    dateReadPinned: data.dateReadPinned,
                });
            }
            return req;
        }, []);
        if (req.length > 0) {
            console.log("Sending messages read to the server: ", JSON.stringify(req));
            api.sendMarkReadRequest(req)
                .catch((err) => {
                console.log("Error calling markMessagesRead", err);
            })
                .finally(() => this.#triggerLoop(api));
        }
        else {
            this.#triggerLoop(api);
        }
    }
    #touch(chatId) {
        const key = chatIdentifierToString(chatId);
        this.#versions.set(key, (this.#versions.get(key) ?? 0) + 1);
    }
    #stateForId(chatId, state) {
        if (!state.has(chatId)) {
            state.set(chatId, new MessagesRead());
        }
        return state.get(chatId);
    }
    markMessageRead(context, messageIndex, messageId) {
        const { state, waiting } = this.value;
        withPausedStores(() => {
            this.#touch(context.chatId);
            const chatState = this.#stateForId(context.chatId, state);
            if (messageId !== undefined && localUpdates.isUnconfirmed(context, messageId)) {
                // if a message is unconfirmed we will just tuck it away until we are told it has been confirmed
                if (!waiting.has(context)) {
                    waiting.set(context, new Map());
                }
                waiting.get(context)?.set(messageId, messageIndex);
            }
            else if (context.threadRootMessageIndex !== undefined) {
                chatState.updateThread(context.threadRootMessageIndex, messageIndex);
            }
            else {
                // Mark the chat as read up to the new messageIndex
                chatState.markReadUpTo(messageIndex);
            }
            this.#store.update((s) => ({ ...s, state, waiting }));
        });
    }
    markReadUpTo(context, index) {
        const { state } = this.value;
        this.#touch(context.chatId);
        const chatState = this.#stateForId(context.chatId, state);
        if (context.threadRootMessageIndex !== undefined) {
            chatState.updateThread(context.threadRootMessageIndex, index);
        }
        else {
            chatState.markReadUpTo(index);
        }
        this.#store.update((s) => ({ ...s, state }));
    }
    markPinnedMessagesRead(chatId, dateLastPinned) {
        const { state } = this.value;
        this.#touch(chatId);
        this.#stateForId(chatId, state).markReadPinned(dateLastPinned);
        this.#store.update((s) => ({ ...s, state }));
    }
    confirmMessage(context, messageIndex, messageId) {
        // this is called when a message is confirmed so that we can move it from
        // the unconfirmed read to the confirmed read. This means that it will get
        // marked as read on the back end
        return withPausedStores(() => {
            if (this.removeUnconfirmedMessage(context, messageId)) {
                this.markMessageRead(context, messageIndex, messageId);
                return true;
            }
            return false;
        });
    }
    removeUnconfirmedMessage(context, messageId) {
        const { waiting } = this.value;
        const deleted = waiting.get(context)?.delete(messageId) ?? false;
        if (deleted) {
            this.#touch(context.chatId);
            this.#store.update((s) => ({ ...s, waiting }));
        }
        return deleted;
    }
    staleThreadCountForChat(chatId, threads) {
        return threads
            .map((thread) => {
            return this.threadReadUpTo(chatId, thread.threadRootMessageIndex) <
                thread.latestMessageIndex
                ? 1
                : 0;
        })
            .reduce((total, n) => total + n, 0);
    }
    threadReadUpTo(chatId, threadRootMessageIndex) {
        const { state, serverState, waiting } = this.value;
        const local = state.get(chatId)?.threads.get(threadRootMessageIndex) ?? -1;
        const server = serverState.get(chatId)?.threads.get(threadRootMessageIndex) ?? -1;
        const unconfirmedReadCount = waiting.get({ chatId, threadRootMessageIndex })?.size ?? 0;
        return Math.max(local + unconfirmedReadCount, server);
    }
    staleThreadsCount(threads) {
        return threads.reduce((total, [chatId, threads]) => {
            const forChat = this.staleThreadCountForChat(chatId, threads);
            return forChat > 0 ? total + forChat : total;
        }, 0);
    }
    unreadThreadMessageCount(chatId, threadRootMessageIndex, latestMessageIndex) {
        return Math.max(latestMessageIndex - this.threadReadUpTo(chatId, threadRootMessageIndex), 0);
    }
    /** A chat's unread messages: none once its latest message has disappeared. */
    unreadMessagesInChat(chat) {
        return latestMessageExpired(chat)
            ? 0
            : this.unreadMessageCount(chat.id, chat.latestMessage?.event.messageIndex);
    }
    unreadMessageCount(chatId, latestMessageIndex) {
        const { state, serverState, waiting } = this.value;
        // previewed chats will not exist in this.serverState
        if (latestMessageIndex === undefined || !serverState.has(chatId)) {
            return 0;
        }
        const readUpToServer = serverState.get(chatId)?.readUpTo;
        const readUpToLocal = state.get(chatId)?.readUpTo;
        const readUpToConfirmed = Math.max(readUpToServer ?? -1, readUpToLocal ?? -1);
        const readUnconfirmedCount = waiting.get({ chatId })?.size ?? 0;
        const total = latestMessageIndex - readUpToConfirmed - readUnconfirmedCount;
        return Math.max(total, 0);
    }
    unreadPinned(chatId, dateLastPinned) {
        const { state, serverState } = this.value;
        const readServer = serverState.get(chatId)?.dateReadPinned ?? BigInt(0);
        const readLocal = state.get(chatId)?.dateReadPinned ?? BigInt(0);
        const dateReadPinned = bigIntMax(readServer, readLocal);
        return (dateLastPinned ?? BigInt(0)) > dateReadPinned;
    }
    getFirstUnreadMessageIndex(chatId, latestMessageIndex) {
        if (latestMessageIndex === undefined) {
            return undefined;
        }
        const { state, serverState, waiting } = this.value;
        const readUpToServer = serverState.get(chatId)?.readUpTo;
        const readUpToLocal = state.get(chatId)?.readUpTo;
        const readUpToConfirmed = Math.max(readUpToServer ?? -1, readUpToLocal ?? -1);
        if (readUpToConfirmed < latestMessageIndex) {
            const readUnconfirmed = waiting.get({ chatId }) ?? new Map();
            const unconfirmedMessageIndexes = [...readUnconfirmed.values()];
            for (let i = readUpToConfirmed + 1; i <= latestMessageIndex; i++) {
                if (!unconfirmedMessageIndexes.includes(i)) {
                    return i;
                }
            }
        }
        return undefined;
    }
    getFirstUnreadMention(chat) {
        return chat.membership.mentions.find((m) => !this.isRead({ chatId: chat.id }, m.messageIndex, m.messageId));
    }
    markAllRead(chat) {
        withPausedStores(() => {
            const latestMessageIndex = chat.latestMessage?.event.messageIndex;
            if (latestMessageIndex !== undefined) {
                if (!this.isRead({ chatId: chat.id }, latestMessageIndex, undefined)) {
                    this.markReadUpTo({ chatId: chat.id }, latestMessageIndex);
                }
                if (chat.kind !== "direct_chat") {
                    for (const thread of chat.membership.latestThreads) {
                        const context = {
                            chatId: chat.id,
                            threadRootMessageIndex: thread.threadRootMessageIndex,
                        };
                        if (!this.isRead(context, thread.latestMessageIndex, undefined)) {
                            this.markReadUpTo(context, thread.latestMessageIndex);
                        }
                    }
                }
            }
        });
    }
    syncWithServer(chatId, readUpTo, threads, dateReadPinned) {
        const { state } = this.value;
        this.#touch(chatId);
        const serverState = new MessagesRead();
        serverState.readUpTo = readUpTo;
        serverState.setThreads(threads);
        serverState.markReadPinned(dateReadPinned);
        const chatState = state.get(chatId);
        if (chatState) {
            if (readUpTo !== undefined &&
                chatState.readUpTo !== undefined &&
                chatState.readUpTo <= readUpTo) {
                chatState.readUpTo = undefined;
            }
            // for each thread we get from the server
            // remove the corresponding thread data from the client state unless the client state is more recent
            threads.forEach((t) => {
                const readUpTo = chatState.threads.get(t.threadRootMessageIndex);
                if (readUpTo !== undefined && readUpTo <= t.readUpTo) {
                    chatState.threads.delete(t.threadRootMessageIndex);
                }
            });
            if (dateReadPinned !== undefined &&
                chatState.dateReadPinned !== undefined &&
                chatState.dateReadPinned <= dateReadPinned) {
                chatState.dateReadPinned = undefined;
            }
        }
        this.#store.update((s) => {
            s.serverState.set(chatId, serverState);
            if (chatState !== undefined && !chatState.empty) {
                s.state.set(chatId, chatState);
            }
            else {
                s.state.delete(chatId);
            }
            return s;
        });
    }
    isRead(context, messageIndex, messageId) {
        const { state, serverState, waiting } = this.value;
        if (messageId !== undefined && localUpdates.isUnconfirmed(context, messageId)) {
            return waiting.get(context)?.has(messageId) ?? false;
        }
        else if (messageId !== undefined && localUpdates.isEphemeral(context, messageId)) {
            return true;
        }
        else if (context.threadRootMessageIndex !== undefined) {
            const serverStateForChat = serverState.get(context.chatId);
            if ((serverStateForChat?.threads.get(context.threadRootMessageIndex) ?? -1) >=
                messageIndex)
                return true;
            const localStateForChat = state.get(context.chatId);
            if ((localStateForChat?.threads.get(context.threadRootMessageIndex) ?? -1) >=
                messageIndex)
                return true;
        }
        else {
            const serverStateForChat = serverState.get(context.chatId);
            if (serverStateForChat?.readUpTo !== undefined &&
                serverStateForChat.readUpTo >= messageIndex)
                return true;
            const localStateForChat = state.get(context.chatId);
            if (localStateForChat?.readUpTo !== undefined &&
                localStateForChat.readUpTo >= messageIndex)
                return true;
        }
        return false;
    }
    combinedUnreadCountForChats(chats) {
        let counts = {
            chats: emptyUnreadCounts(),
            threads: emptyUnreadCounts(),
        };
        for (const chat of chats) {
            if (chat === undefined)
                continue;
            const { unreadMessages, muted, mentions, unreadThreads } = this.#unreadContribution(chat);
            counts = {
                chats: mergeUnreadCounts(unreadMessages, muted, mentions, counts.chats),
                threads: mergeUnreadCounts(unreadThreads, muted, false, counts.threads, unreadThreads),
            };
        }
        return counts;
    }
    // Per-chat unread work is only redone when the chat object or its read state has changed
    #unreadContribution(chat) {
        const key = chatIdentifierToString(chat.id);
        const version = this.#versions.get(key) ?? 0;
        let contribution = this.#contributions.get(key);
        if (contribution === undefined ||
            contribution.chat !== chat ||
            contribution.version !== version) {
            const unreadMessages = this.unreadMessagesInChat(chat);
            contribution = {
                chat,
                version,
                unreadMessages,
                muted: chat.membership.notificationsMuted,
                mentions: unreadMessages > 0 && this.#hasUnreadMentions(chat),
                unreadThreads: this.staleThreadCountForChat(chat.id, chat.membership.latestThreads),
            };
            this.#contributions.set(key, contribution);
        }
        return contribution;
    }
    // Assumes mentions from the server always carry confirmed message ids: isRead also consults
    // localUpdates.isUnconfirmed/isEphemeral, which are not versioned inputs of the contribution
    // cache. A feature that produces local (unconfirmed) mentions must bump the chat's version.
    #hasUnreadMentions(chat) {
        if (chat.kind === "direct_chat")
            return false;
        return (chat.membership.mentions.filter((m) => !this.isRead({ chatId: chat.id }, m.messageIndex, m.messageId)).length > 0);
    }
}
export const messagesRead = new MessageReadTracker();
let networkUnsub;
export function startMessagesReadTracker(api) {
    if (networkUnsub !== undefined) {
        networkUnsub();
    }
    networkUnsub = offlineStore.subscribe((offline) => {
        if (offline) {
            messagesRead.stop();
        }
        else {
            messagesRead.start(api);
        }
    });
}
